<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h2>
          <v-icon class="mr-2">
            mdi-calendar-check
          </v-icon>
          取引先品目単価
        </h2>
      </v-col>
    </v-row>

    <!-- 検索ブロック -->
    <v-row>
      <v-col>
        <validation-observer ref="SalesItemPriceDetailValidation" v-slot="{ invalid }">
          <v-card>
            <v-card-title class="panel-header-color panel-selectable">
              <strong>
                <small>
                  <v-icon class="panel-header-color">
                    mdi-magnify
                  </v-icon>
                  &nbsp;条件を指定して検索
                </small>
              </strong>
            </v-card-title>
            <v-spacer />
            <v-card-text>
              <v-row>
                <!-- 事業者 -->
                <v-col cols="12" md="4">
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    rules="required"
                    name="事業者"
                  >
                    <v-text-field
                      v-model="supplierName"
                      :error-messages="errors"
                      clearable
                      dense
                      filled
                      hide-details="auto"
                      label="事業者"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      rounded
                      readonly
                    >
                      <template v-slot:append-outer>
                        <choose-supplier-button
                          :button-caption="`事業者を選択`"
                          :small-button="true"
                          :without-invisible="true"
                          with-none-location
                          @choseSupplierCallback="choseSupplierCallback"
                        />
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <!-- /事業者 -->
                <!-- 表示基準日 -->
                <v-col cols="12" md="4">
                  <cs-date-picker
                    :date.sync="searchForms.reference_date"
                    @dateChangedCallback="referenceDateChangedCallback"
                  >
                    <template v-slot="{ on, attrs }">
                      <validation-provider
                        v-slot="{ errors }"
                        class="requiredMark"
                        rules="required"
                        name="表示基準日"
                      >
                        <v-text-field
                          v-model="searchForms.reference_date"
                          v-bind="attrs"
                          v-on="on"
                          :error-messages="errors"
                          append-icon="mdi-calendar"
                          clearable
                          dense
                          hide-details="auto"
                          label="表示基準日"
                          outlined
                          persistent-hint
                          persistent-placeholder
                          placeholder="日付を選択"
                          readonly
                          rounded
                          style="font-size: 80%"
                        />
                      </validation-provider>
                    </template>
                  </cs-date-picker>
                </v-col>
                <!-- /表示基準日 -->
                <!-- 売上品目名 -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="searchForms.sales_item_name"
                    clearable
                    dense
                    hide-details="auto"
                    label="売上品目名"
                    placeholder="部分一致で検索"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    rounded
                  />
                </v-col>
                <!-- /売上品目名 -->
                <v-col cols="12" md="1" class="text-right">
                  <v-btn
                    :disabled="invalid"
                    color="primary"
                    @click="searchButtonClickEvent"
                  >
                    <v-icon>mdi-magnify</v-icon>&nbsp;検索
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </validation-observer>
      </v-col>
    </v-row>
    <!-- /検索ブロック -->

    <v-row>
      <v-col>
        <v-card-actions>
          <v-spacer />

          <output-reservation-dialog
            section="supplierable_sales_item_price"
            :enable-blink="blinkingButton"
            @stopBlinkCallback="blinkingButton = false"
          />

          <v-menu
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="info"
                dark
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-file-download-outline</v-icon>&nbsp;CSV出力
              </v-btn>&emsp;
            </template>
            <v-list dense>
              <v-list-item
                :disabled="!supplierName"
                link
                @click="exportButtonClickEvent"
              >
                <v-list-item-title>
                  個別出力
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                link
                @click="bulkExportButtonClickEvent"
              >
                <v-list-item-title>
                  一括出力
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-actions>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <validation-observer ref="emissionItemUnitPriceValidator">
          <v-form>
            <v-card class="rounded-card" flat>
              <v-card-text>
                <v-data-table
                  :headers="tableHeaders"
                  :items="salesItemPriceDetails"
                  :items-per-page="itemsPerPage"
                  :page.sync="page"
                  dense
                  disable-sort
                  hide-default-footer
                  @page-count="pageCount = $event"
                >
                  <template v-slot:[`item.enable`]="{ item }">
                    <v-checkbox
                      v-model="item.enable"
                      dense
                    />
                  </template>
                  <template v-slot:[`item.usage_category`]="{ index, item }">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{ required: true }"
                      :vid="`usage_category_${getGlobalIndex(index)}`"
                      name="利用区分"
                    >
                      <v-select
                        v-model="item.usage_category"
                        :error-messages="errors"
                        :items="usageCategories"
                        :disabled="!item.enable"
                        :filled="!item.enable"
                        dense
                        outlined
                        hide-details="auto"
                        style="font-size: small"
                      />
                    </validation-provider>

                  </template>
                  <template v-slot:[`item.sales_item_price`]="{ item }">
                    <div v-if="item.sales_item_price_code">
                      <div>
                        {{ `${item.sales_item_price_code}:${item.sales_item_price_name}` }}&nbsp;
                      </div>
                    </div>
                    <small class="text--disabled">{{ item.reference_date | date_format }}</small>
                  </template>
                  <template v-slot:[`item.standard_unit_price`]="{ item }">
                    {{ item.standard_unit_price | comma_format }}
                  </template>
                  <template v-slot:[`item.unit_price`]="{index, item}">
                    <div v-if="item.enable && item.usage_category === 'individual'">
                      <validation-provider
                        v-slot="{ errors }"
                        :rules="{
                        required: (item.enable && item.usage_category === 'individual'),
                        double: true,
                        min_value: (item.enable && item.usage_category === 'individual') ? -9999999.999 : false,
                        max_value: 9999999.999,
                        quantityWithDecimal: { int_digits: 7, dec_digits: 3 }
                    }"
                        :vid="`unit_price_${getGlobalIndex(index)}`"
                        name="個別単価"
                      >
                        <v-text-field
                          v-model="item.unit_price"
                          :disabled="!(item.enable && item.usage_category === 'individual')"
                          :error-messages="errors"
                          :filled="!(item.enable && item.usage_category === 'individual')"
                          :show-size="10000"
                          dense
                          hide-details="auto"
                          outlined
                          persistent-placeholder
                          reverse
                          required
                          type="number"
                          style="font-size: small"
                          @keydown.69.prevent
                        />
                      </validation-provider>
                    </div>
                  </template>
                  <template v-slot:[`item.tax_type`]="{ item }">
                    {{ item.tax_type | tax_type }}
                  </template>
                  <template v-slot:[`item.item`]="{ item }">
                    <span v-if="item.item_code">
                      {{ `${item.item_code}:${item.item_name}` }}
                    </span>
                  </template>
                  <template v-slot:[`item.waste_name`]="{index, item}">
                    <div v-if="item.enable && item.usage_category === 'individual'">
                      <validation-provider
                        v-slot="{ errors }"
                        :vid="`waste_name_${getGlobalIndex(index)}`"
                        name="廃棄物の名称"
                        rules="max:30"
                      >
                        <v-text-field
                          v-model="item.waste_name"
                          :disabled="!(item.enable && item.usage_category === 'individual')"
                          :error-messages="errors"
                          :filled="!(item.enable && item.usage_category === 'individual')"
                          dense
                          hide-details="auto"
                          outlined
                          style="font-size: small"
                          persistent-placeholder
                        />
                      </validation-provider>
                    </div>
                    <span v-else>{{ item.reflected_waste_name }}</span>
                  </template>
                  <template v-slot:[`item.transport_route`]="{index, item}">
                    <div v-if="item.enable && item.usage_category === 'individual'">
                      <validation-provider
                        v-slot="{ errors }"
                        :vid="`transport_route_${getGlobalIndex(index)}`"
                        name="運搬ルート"
                      >
                        <v-select
                          v-model="item.transport_route_id"
                          :error-messages="errors"
                          :items="transportRoutes"
                          :filled="!(item.enable && item.usage_category === 'individual')"
                          clearable
                          item-value="id"
                          item-text="name"
                          hide-details="auto"
                          dense
                          outlined
                          style="font-size: small"
                          persistent-placeholder
                        />
                      </validation-provider>
                    </div>
                    <span v-else>{{ item.reflected_transport_route_name }}</span>
                  </template>
                </v-data-table>
                <v-row>
                  <v-col>
                    <v-pagination
                      v-model="page"
                      :length="pageCount"
                      :total-visible="totalVisible"
                      circle
                      prev-icon="mdi-menu-left"
                      next-icon="mdi-menu-right"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  @click="backButtonClickEvent"
                >
                  戻る
                </v-btn>
                <VButtonProcessing
                  :disabled="salesItemPriceDetails.length <= 0"
                  :onclick="registerButtonClickEvent"
                  color="primary"
                  elevation="5"
                >
                  登録
                </VButtonProcessing>
              </v-card-actions>
            </v-card>
          </v-form>
        </validation-observer>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import ChooseSupplierButton from '@/driver/components/ChooseSupplierButton.vue'
import VButtonProcessing from '../../../../../components/VButtonProcessing'
import DownloadMixin from '../../../../../mixins/download-mixin'
import OutputReservationDialog from "@/driver/components/OutputReservationDialog.vue";

export default {
  name: 'SupplierSalesItemPrices',
  components: {
    OutputReservationDialog,
    ChooseSupplierButton,
    VButtonProcessing
  },
  mixins: [
    DownloadMixin,
  ],
  props: {},
  data() {
    return {
      page: 1,
      pageCount: 0,
      totalVisible: 7,
      itemsPerPage: 10,
      panel: [0],
      searchForms: {
        /** 取引先の詳細情報 */
        suppliable_id: null,
        suppliable_type: null,
        reference_date: this.$dayjs().format("YYYY-MM-DD"),
        sales_item_name: null,
      },
      supplierName: null,
      tableHeaders: [
        { value: 'enable', text: '利用有無' },
        { value: 'usage_category', text: '利用区分', width: '10%' },
        { value: 'sales_item_price', text: '売上品目' },
        { value: 'standard_unit_price', text: '標準単価', align: 'end' },
        { value: 'unit_price', text: '個別単価', width: '10%', align: 'end' },
        { value: 'sales_item_unit_name', text: '単位', align: 'left' },
        { value: 'tax_type', text: '課税区分' },
        { value: 'item', text: 'JWNET取扱品目' },
        { value: 'waste_name', text: '廃棄物の名称', width: '13%' },
        { value: 'transport_route', text: 'ルート', width: '12%' }
      ],
      salesItemPriceDetails: [],
      usageCategories: [
        { value: 'standard', text: '標準' },
        { value: 'individual', text: '個別' },
      ],
      transportRoutes: [],
      blinkingButton: false,
    }
  },
  computed: {},
  watch: {
    /**
     * 表示基準日の監視
     */
    'searchForms.reference_date': function () {
      if (this.supplierName) {
        this.searchButtonClickEvent()
      }
    },
  },
  created() {
    this.fetchTransportRoutes({ without_invisible: true, first_trader_owned: true, self_emission: true, self_emission_include_own: true }).then((content) => {
      this.transportRoutes = content.transport_routes
    })
  },
  methods: {
    ...mapActions('DriverMasterSearchTransportRoute', {
      fetchTransportRoutes: 'search'
    }),
    ...mapActions('DriverMasterSupplierSalesItemPrice', {
      fetchSalesItemPrices: 'index',
      registerSalesItemPrices: 'create'
    }),
    ...mapActions('DriverMasterSupplierSalesItemPriceOutputReservation', {
      outputReserve: 'create',
    }),
    /**
     * 検索ボタン クリックイベント
     */
    searchButtonClickEvent () {
      this.$refs.SalesItemPriceDetailValidation.validate().then((result) => {
        if(result) {
          this.resetResult()
          let params = {
            supplier_id: this.searchForms.supplier_id,
            supplier_type: this.searchForms.supplier_type,
            reference_date: this.searchForms.reference_date,
            sales_item_name: this.searchForms.sales_item_name,
          }
          this.fetchSalesItemPrices(params).then((content) => {
            this.salesItemPriceDetails = content.sales_item_price_details
          })
        }
      })
    },
    /**
     * 戻るボタン クリックイベント
     */
    backButtonClickEvent () {
      this.$router.push({name: 'DriverMasterSalesItemPrices'})
    },
    /**
     * 登録ボタン クリックイベント
     * @return {Promise<unknown>}
     */
    registerButtonClickEvent () {
      const $self = this
      return new Promise((resolve) => {
        setTimeout(() => {
          this.$refs.emissionItemUnitPriceValidator.validateWithInfo().then(({ isValid, errors }) => {
            if(isValid) {
              $self.sendRegistrationInformation()
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
            }
          })
          resolve();
        }, 1000);
      });
    },
    /**
     * 入力内容の送信
     */
    sendRegistrationInformation () {
      let details = []
      for (const salesItemPriceDetail of this.salesItemPriceDetails) {
        let detail = {
          id: salesItemPriceDetail.id,
          sales_item_price_id: salesItemPriceDetail.sales_item_price_id,
          enable: salesItemPriceDetail.enable,
          usage_category: salesItemPriceDetail.usage_category,
        }
        if (salesItemPriceDetail.enable && salesItemPriceDetail.usage_category === 'individual') {
          detail["unit_price"] = salesItemPriceDetail.unit_price;
          detail["waste_name"] = salesItemPriceDetail.waste_name;
          detail["transport_route_id"] = salesItemPriceDetail.transport_route_id;
        }
        details.push(detail)
      }

      const parameters = {
        supplier_id: this.searchForms.supplier_id,
        supplier_type: this.searchForms.supplier_type,
        sales_item_price_details: details,
        reference_date: this.searchForms.reference_date
      }
      this.registerSalesItemPrices({ params: parameters }).then(() => {
        this.searchButtonClickEvent()
      })
    },
    /**
     * 一覧情報のリセット
     */
    resetResult () {
      this.page = 1
      this.pageCount = 0
      this.salesItemPriceDetails = []
    },
    /**
     * 取引先選択ボタンからのダイアログのコールバック
     * @param item
     */
    choseSupplierCallback (item) {
      this.supplierName = item.name
      this.searchForms.supplier_id = item.id
      this.searchForms.supplier_type = item.type
      setTimeout(() => {
        this.searchButtonClickEvent()
      }, 500)
    },
    exportButtonClickEvent () {
      const parameters = {
        supplier_id: this.searchForms.supplier_id,
        supplier_type: this.searchForms.supplier_type,
        reference_date: this.searchForms.reference_date,
        layout: 'select'
      }
      this.outputReserve({ conditions: parameters }).then(() => {
        this.blinkingButton = true
      })
    },
    bulkExportButtonClickEvent () {
        const parameters = {
        supplier_id: '',
        supplier_type: '',
        reference_date: this.searchForms.reference_date,
        layout: ''
      }
      this.outputReserve({ conditions: parameters }).then(() => {
        this.blinkingButton = true
        // this.downloadCsv(content, '取引先品目単価')
      })
    },
    getGlobalIndex(index) {
      return index + this.itemsPerPage * (this.page - 1);
    },
    /**
     * 表示基準日変更　コールバック
     */
    referenceDateChangedCallback (value) {
      this.searchForms.reference_date = value;
    },
  }
}
</script>

<style scoped lang="scss">
.panel-header-color {
  color: #0D74BA !important;
  & .v-expansion-panel-header__icon {
    & .v-icon {
      color: #0D74BA !important;
      &::before {
        color: #0D74BA !important;
      }
    }
  }
}
.panel-selectable {
  background-color: #DCE7EB;
}
</style>

<style lang="scss">
.supplierBlock {
  & .v-input__append-inner {
    margin-top: 6px !important;
  }
}
</style>

<style lang="scss">
.supplierBlock {
  & .v-input__append-inner {
    margin-top: 6px !important;
  }
}
</style>
